import { apiConfig } from "~/config/api";
import { apiMakeRawRequest } from "~/lib/apiClient";

import { getRequestContext } from "./requestContextStore";

/**
 * Single source of truth for service routing — `resolveBaseUrl` and the
 * service-crossing guard (`servicePrefixOf`) both read this same table.
 */
const SERVICE_ROUTES: ReadonlyArray<{ prefix: string; baseUrl: () => string }> = [
  { prefix: "/schema-registry-service", baseUrl: () => apiConfig.schemaRegistryBaseUrl },
  { prefix: "/record-service", baseUrl: () => apiConfig.recordServiceBaseUrl },
  { prefix: "/document-service", baseUrl: () => apiConfig.documentServiceBaseUrl },
  { prefix: "/workflow-service", baseUrl: () => apiConfig.workflowServiceBaseUrl },
  { prefix: "/lifecycle-engine-service", baseUrl: () => apiConfig.engineBaseUrl },
  { prefix: "/flavour-provisioning", baseUrl: () => apiConfig.verticalProvisionerBaseUrl },
];

const servicePrefixOf = (path: string): string | undefined =>
  SERVICE_ROUTES.find((route) => path.startsWith(route.prefix))?.prefix;

/**
 * The path as the CALLER wrote it — no dot-segment resolution, no percent
 * decoding. An absolute URL loses its scheme and authority; anything else is
 * already a path.
 */
const rawPathOf = (url: string): string =>
  url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "").split(/[?#]/)[0] ?? "";

/**
 * Refuse a request whose resolved path lands in a different service than the one
 * its raw URL named. Silent re-routing is the dangerous outcome, so this throws
 * rather than falling back to the raw path: a caller that hits this has an
 * unescaped path parameter, and answering from the "right" service would hide it.
 */
const assertNoServiceCrossing = (url: string, resolvedPath: string): void => {
  const requested = servicePrefixOf(rawPathOf(url));
  const resolved = servicePrefixOf(resolvedPath);

  if (requested === resolved) {
    return;
  }

  throw new Error(
    `[custom-fetch] Refusing a request that leaves its service: ${url} resolves to ` +
      `${resolvedPath} (${requested ?? "no service prefix"} → ${resolved ?? "no service prefix"}). ` +
      `A path parameter almost certainly needs encodeURIComponent().`,
  );
};

/**
 * Custom fetch adapter for Orval-generated code.
 *
 * Orval generates calls like `customFetch<T>(url, { method, body, ... })`.
 * This adapter delegates to the existing ApiClient, which provides:
 * - Bearer token injection
 * - X-Tenant-Id / X-Api-Key / X-Service-Name headers
 * - 2min timeout via AbortController
 * - 401 retry with automatic token refresh
 * - Centralized ApiError throwing
 *
 * Tenant context is read from the module-level requestContextStore, which is
 * kept in sync by the AuthContext provider.
 */
export const customFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
  // Orval builds the full path from the spec (e.g. /schema-registry-service/v1/schema).
  // Extract pathname + search so ApiClient can prepend the correct base URL.
  let endpoint: string;
  let resolvedPath: string | null = null;

  try {
    const parsed = new URL(url, window.location.origin);

    endpoint = parsed.pathname + parsed.search;
    resolvedPath = parsed.pathname;
  } catch {
    endpoint = url;
  }

  if (resolvedPath !== null) {
    // `new URL()` RESOLVES dot segments, and no generated URL builder escapes its
    // path parameters (there is not one `encodeURIComponent` in
    // `app/api/generated/`). So a record pk of `../../../../flavour-provisioning/v1/x`
    // — which arrives from a route param, i.e. from a link someone can send —
    // parses to that path exactly, and everything downstream reads the RESOLVED
    // path: `resolveBaseUrl` picks the service, and the `/flavour-provisioning`
    // branch below attaches the provisioner's `x-api-key`. The request would
    // leave the browser aimed at a different service, carrying that service's
    // credentials alongside the user's bearer token.
    //
    // Comparing the service prefix before and against after resolution closes
    // that whether or not a call site remembered to encode — and encoding does
    // NOT make this check fire, because `%2F` never resolves.
    assertNoServiceCrossing(url, resolvedPath);
  }

  const baseUrl = resolveBaseUrl(endpoint);
  const baseRequestContext = getRequestContext();

  // Record-service (BOS) is not view-scoped in the backend — DalPgMTDynamicService.getView()
  // hardcodes "default" for record-service regardless of the x-view header. Don't send
  // the header on record-service calls.
  const isRecordService = endpoint.startsWith("/record-service/");
  const isFlavourProvisioning = endpoint.startsWith("/flavour-provisioning");
  const requestContext = {
    ...baseRequestContext,
    ...(isRecordService ? { sendView: false } : {}),
    // Flavour provisioning sits behind the same gateway as other services and
    // requires x-api-key / x-cpes-service-name. custom-fetch overlays the
    // provisioner identity (dedicated env, else VITE_API_KEY, else local
    // DUMMY-123 defaults) so cloud builds do not ship compose-only credentials.
  };

  const method = (options.method ?? "GET").toUpperCase();
  const requestHeaders =
    options.headers instanceof Headers
      ? Object.fromEntries(options.headers)
      : (options.headers as Record<string, string> | undefined);

  const provisioningHeaders = endpoint.startsWith("/flavour-provisioning")
    ? {
        "x-api-key": apiConfig.verticalProvisionerApiKey,
        "x-cpes-service-name": apiConfig.verticalProvisionerServiceName,
      }
    : undefined;

  const response = await apiMakeRawRequest(
    baseUrl,
    endpoint,
    {
      method,
      body: options.body,
      headers: { ...requestHeaders, ...provisioningHeaders },
      // Rebuilding `options` here drops anything not named — leave `signal` out
      // and every generated call becomes uncancellable, which matters most on
      // this path: the per-row dereference fan-out.
      ...(options.signal ? { signal: options.signal } : {}),
    },
    requestContext,
    {
      // Orval GET handlers are typed with 404 branches — don't throw so callers
      // can return `{ status: 404 }` instead of treating "no relationships" as fatal.
      throwOnError: method !== "GET",
      // Flavour apply holds the connection until the diff is written. `0`
      // disables the client abort timer (`apiConfig.provisioningTimeout`).
      timeoutMs: isFlavourProvisioning ? apiConfig.provisioningTimeout : undefined,
    },
  );

  // Orval generates response types like { data: T, status: number, headers: Headers }.
  // We must construct this wrapper so generated hooks receive correctly shaped data.

  // Empty-body status codes
  if (response.status === 204 || response.status === 205 || response.status === 304) {
    return { data: undefined, status: response.status, headers: response.headers } as T;
  }

  const contentType = response.headers.get("content-type") ?? "";

  // Binary responses (images, PDFs, octet-stream)
  if (
    contentType.startsWith("application/octet-stream") ||
    contentType.startsWith("image/") ||
    contentType.startsWith("application/pdf")
  ) {
    const blob = await response.blob();

    return { data: blob, status: response.status, headers: response.headers } as T;
  }

  // Text responses (non-JSON)
  if (contentType.startsWith("text/") && !contentType.includes("json")) {
    const text = await response.text();

    return { data: text, status: response.status, headers: response.headers } as T;
  }

  // JSON responses (default path)
  const text = await response.text();
  if (!text) {
    return { data: undefined, status: response.status, headers: response.headers } as T;
  }

  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    // Unparseable body — return as text
    return { data: text, status: response.status, headers: response.headers } as T;
  }

  // Unwrap the platform's PURE { data: T } envelope — and only that. Bodies
  // where `data` has siblings are themselves the payload (the collection
  // table-mode envelope `{ data, total, facets, meta }` — unwrapping it ate
  // `total`, the facet buckets, and `meta.engine`).
  const isPureDataWrapper =
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    "data" in raw &&
    Object.keys(raw).length === 1;
  const data = isPureDataWrapper ? (raw as Record<string, unknown>).data : raw;

  return { data, status: response.status, headers: response.headers } as T;
};

/**
 * Maps a request path to the correct backend microservice base URL.
 */
const resolveBaseUrl = (endpoint: string): string => {
  const route = SERVICE_ROUTES.find((r) => endpoint.startsWith(r.prefix));

  if (route) {
    return route.baseUrl();
  }

  throw new Error(
    `[custom-fetch] Unknown service prefix in endpoint: ${endpoint}. ` +
      `Expected one of: ${SERVICE_ROUTES.map((r) => r.prefix).join(", ")}`,
  );
};
